import { Modal } from './Modal';
import { Document } from './DocumentCard';

interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
}

/**
 * PDFPreviewModal component - Nested modal for PDF preview
 * 
 * Features:
 * - Mock PDF preview (placeholder image)
 * - Scroll support for document content
 * - Close button to return to document list
 * - Responsive layout
 * - Full-screen on mobile, centered on desktop
 */
export const PDFPreviewModal = ({ isOpen, onClose, document }: PDFPreviewModalProps) => {
  if (!document) return null;

  // Get document type display name
  const getDocumentTypeName = (type: Document['type']): string => {
    const typeNames: Record<Document['type'], string> = {
      allocation_declaration: 'Allocation Declaration',
      inspection_certificate: 'Inspection Certificate',
      warehouse_receipt: 'Warehouse Receipt',
      certificate_of_origin: 'Certificate of Origin',
      other: 'Other Document',
    };
    return typeNames[type];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getDocumentTypeName(document.type)}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        {/* PDF Preview Container */}
        <div className="w-full border border-border rounded-lg overflow-hidden bg-background-secondary">
          {/* Mock PDF Preview - Placeholder image */}
          <div className="w-full h-[600px] flex items-center justify-center bg-background-secondary">
            {document.url ? (
              <img
                src={document.url}
                alt={getDocumentTypeName(document.type)}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="text-center p-8">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-base text-text-secondary">
                  PDF Preview Placeholder
                </p>
                <p className="text-sm text-text-tertiary mt-2">
                  {document.fileName}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Document Info */}
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>File: {document.fileName}</span>
          <span>Status: {document.status}</span>
        </div>
      </div>
    </Modal>
  );
};

